import axios from 'axios';
import apiConfig from '../config/apiConfig';
import { captureException } from '../utils/errorTracking';

const API_BASE_URL = apiConfig.BASE_URL;

// Get all articles with pagination and filters (multilingual support)
export const getAllArticles = async (params = {}) => {
  try {
    const { 
      page = 1, 
      limit = 10, 
      category, 
      author, 
      featured, 
      trending,
      lang,
      region
    } = params;
    const queryParams = new URLSearchParams();
    
    if (page) queryParams.append('page', page);
    if (limit) queryParams.append('limit', limit);
    if (category) queryParams.append('category', category);
    if (author) queryParams.append('author', author);
    if (featured) queryParams.append('featured', featured);
    if (trending) queryParams.append('trending', trending);
    if (lang) queryParams.append('lang', lang);
    if (region) queryParams.append('region', region);
    
    const response = await axios.get(`${API_BASE_URL}/articles?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    captureException(error, {
      tags: {
        service: 'articleService',
        action: 'getAllArticles'
      }
    });
    throw error;
  }
};

// Get article by slug (multilingual support with region)
export const getArticleBySlug = async (slug, lang = null, region = null) => {
  try {
    const params = {};
    if (lang) params.lang = lang;
    if (region) params.region = region;
    const response = await axios.get(`${API_BASE_URL}/articles/${slug}`, { params });
    return response.data;
  } catch (error) {
    captureException(error, {
      tags: {
        service: 'articleService',
        action: 'getArticleBySlug',
        slug: slug
      }
    });
    throw error;
  }
};

// Track article view
export const trackArticleView = async (slug, lang = null) => {
  try {
    const params = {};
    if (lang) params.lang = lang;
    const response = await axios.post(`${API_BASE_URL}/articles/${slug}/view`, null, { params });
    return response.data;
  } catch (error) {
    captureException(error, {
      tags: {
        service: 'articleService',
        action: 'trackArticleView',
        slug: slug
      }
    });
    // Don't throw - view tracking failure shouldn't break the page
    return null;
  }
};

// Get top articles (multilingual support)
export const getTopArticles = async (limit = 5, params = {}) => {
  try {
    const { lang, region } = params;
    const queryParams = new URLSearchParams();
    queryParams.append('limit', limit);
    if (lang) queryParams.append('lang', lang);
    if (region) queryParams.append('region', region);

    const response = await axios.get(`${API_BASE_URL}/articles/top?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching top articles:', error);
    throw error;
  }
};

// Get popular articles (multilingual support)
export const getPopularArticles = async (limit = 10, params = {}) => {
  try {
    const { lang, region } = params;
    const queryParams = new URLSearchParams();
    queryParams.append('limit', limit);
    if (lang) queryParams.append('lang', lang);
    if (region) queryParams.append('region', region);

    const response = await axios.get(`${API_BASE_URL}/articles/popular?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching popular articles:', error);
    throw error;
  }
};

// Get trending articles (multilingual support)
export const getTrendingArticles = async (limit = 10, params = {}) => {
  try {
    const { lang, region } = params;
    const queryParams = new URLSearchParams();
    queryParams.append('limit', limit);
    if (lang) queryParams.append('lang', lang);
    if (region) queryParams.append('region', region);
    
    const response = await axios.get(`${API_BASE_URL}/articles/trending?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching trending articles:', error);
    throw error;
  }
};

// Get similar articles (same category + shared tags)
export const getSimilarArticles = async (params = {}) => {
  try {
    const { articleId, slug, limit = 3, lang, region } = params;
    const queryParams = new URLSearchParams();
    if (articleId) queryParams.append('articleId', articleId);
    if (slug) queryParams.append('slug', slug);
    queryParams.append('limit', String(limit));
    if (lang) queryParams.append('lang', lang);
    if (region) queryParams.append('region', region);

    const response = await axios.get(`${API_BASE_URL}/articles/similar?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching similar articles:', error);
    throw error;
  }
};

// Get featured article
export const getFeaturedArticle = async (limit = 1) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/articles/featured`, {
      params: { limit }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching featured article:', error);
    throw error;
  }
};

// Create article (Admin only)
export const createArticle = async (articleData) => {
  try {
    const token = getAuthToken();
    const response = await axios.post(`${API_BASE_URL}/articles`, articleData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error creating article:', error);
    throw error;
  }
};

// Update article (Admin only)
export const updateArticle = async (id, articleData) => {
  try {
    const token = getAuthToken();
    const response = await axios.put(`${API_BASE_URL}/articles/${id}`, articleData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error updating article:', error);
    throw error;
  }
};

// Delete article (Admin only)
export const deleteArticle = async (id) => {
  try {
    const token = getAuthToken();
    const response = await axios.delete(`${API_BASE_URL}/articles/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting article:', error);
    throw error;
  }
};

// Helper function to get auth token
const getAuthToken = () => {
  try {
    const persistRoot = localStorage.getItem('persist:root');
    if (persistRoot) {
      const parsed = JSON.parse(persistRoot);
      if (parsed.auth) {
        const authState = JSON.parse(parsed.auth);
        return authState.token;
      }
    }
  } catch (e) {
    console.warn('Could not read token from redux-persist:', e);
  }
  
  return localStorage.getItem('token') || sessionStorage.getItem('token');
};


